import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_b7533cee-6cc1-4a41-9e88-4b20184fec89/artifacts/eixt3fs2_STM%20mobile%20logo.png';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/app');
    } catch (error) {
      const errorCode = error.response?.data?.detail?.code;
      const errorEmail = error.response?.data?.detail?.email;
      const message = error.response?.data?.detail?.error || 'Login failed';
      
      // Handle verification required - redirect to check-email page
      if (errorCode === 'EMAIL_VERIFICATION_REQUIRED') {
        toast.error('Please verify your email first');
        navigate('/check-email', { state: { email: errorEmail || email } });
        return;
      }
      
      // Handle expired verification - need to sign up again
      if (errorCode === 'VERIFICATION_EXPIRED') {
        toast.error('Your verification has expired. Please sign up again.');
        navigate('/signup');
        return;
      }
      
      // Handle disabled account
      if (errorCode === 'ACCOUNT_DISABLED') {
        toast.error('Your account has been disabled. Please contact support.');
        return;
      }
      
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4 py-12"
      style={{
        background: 'linear-gradient(135deg, #0B0F1A 0%, #0E1022 50%, #12162B 100%)'
      }}
    >
      {/* Subtle background glow */}
      <div 
        className="absolute inset-0 overflow-hidden pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at 50% 0%, rgba(124, 92, 255, 0.08) 0%, transparent 60%)'
        }}
      />

      <div className="w-full max-w-md relative z-10">
        {/* Logo/Brand */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-4">
            <img 
              src={LOGO_URL} 
              alt="ScopeTheMarket" 
              className="h-10 w-auto"
              style={{ filter: 'drop-shadow(0 0 10px rgba(59, 130, 246, 0.5))' }}
            />
            <span className="text-xl font-semibold" style={{ color: '#F4F4F5' }}>
              ScopeTheMarket
            </span>
          </Link>
          <h1 
            className="text-3xl font-bold mb-2"
            style={{ color: '#F4F4F5' }}
          >
            Welcome back
          </h1>
          <p style={{ color: '#71717A' }}>
            Sign in to access your dashboard
          </p>
        </div>

        {/* Auth Card */}
        <div 
          className="rounded-2xl p-8"
          style={{
            background: 'rgba(20, 24, 40, 0.8)',
            border: '1px solid rgba(124, 92, 255, 0.15)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 80px rgba(124, 92, 255, 0.05)'
          }}
        >
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email Input */}
            <div>
              <label 
                className="block text-sm font-medium mb-2"
                style={{ color: '#A1A1AA' }}
              >
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-xl text-sm transition-all duration-200 outline-none"
                style={{
                  background: 'rgba(15, 18, 30, 0.8)',
                  border: '1px solid rgba(113, 113, 122, 0.2)',
                  color: '#F4F4F5'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'rgba(124, 92, 255, 0.5)';
                  e.target.style.boxShadow = '0 0 0 3px rgba(124, 92, 255, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(113, 113, 122, 0.2)';
                  e.target.style.boxShadow = 'none';
                }}
                placeholder="you@example.com"
                data-testid="login-email-input"
              />
            </div>

            {/* Password Input */}
            <div>
              <label 
                className="block text-sm font-medium mb-2"
                style={{ color: '#A1A1AA' }}
              >
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-4 py-3 pr-12 rounded-xl text-sm transition-all duration-200 outline-none"
                  style={{
                    background: 'rgba(15, 18, 30, 0.8)',
                    border: '1px solid rgba(113, 113, 122, 0.2)',
                    color: '#F4F4F5'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'rgba(124, 92, 255, 0.5)';
                    e.target.style.boxShadow = '0 0 0 3px rgba(124, 92, 255, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(113, 113, 122, 0.2)';
                    e.target.style.boxShadow = 'none';
                  }}
                  placeholder="Enter your password"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg transition-colors"
                  style={{ color: '#71717A' }}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Forgot Password Link */}
            <div className="text-right">
              <Link
                to="/forgot-password"
                className="text-sm transition-colors"
                style={{ color: '#94A3B8' }}
                onMouseEnter={(e) => e.target.style.color = '#3B82F6'}
                onMouseLeave={(e) => e.target.style.color = '#94A3B8'}
              >
                Forgot password?
              </Link>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl text-white font-medium text-sm transition-all duration-200 flex items-center justify-center gap-2"
              style={{
                background: loading 
                  ? 'rgba(124, 92, 255, 0.5)' 
                  : 'linear-gradient(135deg, #7C5CFF 0%, #6B4FE0 100%)',
                boxShadow: loading 
                  ? 'none' 
                  : '0 4px 20px rgba(124, 92, 255, 0.3), 0 0 40px rgba(124, 92, 255, 0.1)'
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.boxShadow = '0 4px 25px rgba(124, 92, 255, 0.4), 0 0 60px rgba(124, 92, 255, 0.15)';
                  e.target.style.transform = 'translateY(-1px)';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.target.style.boxShadow = '0 4px 20px rgba(124, 92, 255, 0.3), 0 0 40px rgba(124, 92, 255, 0.1)';
                  e.target.style.transform = 'translateY(0)';
                }
              }}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div 
              className="absolute inset-0 flex items-center"
            >
              <div className="w-full" style={{ borderTop: '1px solid rgba(113, 113, 122, 0.2)' }} />
            </div>
            <div className="relative flex justify-center">
              <span 
                className="px-3 text-xs"
                style={{ 
                  background: 'rgba(20, 24, 40, 0.8)',
                  color: '#71717A' 
                }}
              >
                New to ScopeTheMarket?
              </span>
            </div>
          </div>

          {/* Sign Up Link */}
          <Link
            to="/signup"
            className="block w-full py-3 rounded-xl text-center font-medium text-sm transition-all duration-200"
            style={{
              border: '1px solid rgba(124, 92, 255, 0.3)',
              color: '#A78BFA'
            }}
            onMouseEnter={(e) => {
              e.target.style.borderColor = 'rgba(124, 92, 255, 0.5)';
              e.target.style.background = 'rgba(124, 92, 255, 0.05)';
            }}
            onMouseLeave={(e) => {
              e.target.style.borderColor = 'rgba(124, 92, 255, 0.3)';
              e.target.style.background = 'transparent';
            }}
            data-testid="signup-link"
          >
            Create an account
          </Link>
        </div>

        {/* Footer */}
        <p 
          className="text-center mt-6 text-xs"
          style={{ color: '#52525B' }}
        >
          By signing in, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
};

export default Login;
